/**
 * @format
 */
import { Injectable } from "@angular/core";
import { EditorService } from "./editor.service";
import {
   AlignmentType,
   RangeOffset,
   SelectedElement,
   SelectionType,
   StackItemNode,
   StylingStatus,
} from "../types/editor-types";
import { BaseToolService } from "./base-tool.service";
import { ToolAbstractService } from "./tool-abstract.service";
import { ToolExtraArguments } from "../types/tool-extra-arguments";

@Injectable()
export class AlignmentToolService extends BaseToolService implements ToolAbstractService {
   readonly AUX_ROOT_ID = "AUX_ROOT_ID";
   readonly START_BOUNDARY_ID = "START_NODE_ID";
   readonly END_BOUNDARY_ID = "END_NODE_ID";
   readonly PARAMETER_ID = "PARAMETER_ID";

   private _affectedNodes: Array<Node>;
   private _stackParentNodes: Array<StackItemNode>;
   private _isParameter = false;
   private _alignment: string;
   private _firstNode: Node;
   private _lastNode: Node;

   constructor(private editorService: EditorService) {
      super();
      this.initializeVariables();
   }

   getStyledElementsAsString(selection: Partial<SelectedElement>, extraArgument: Partial<ToolExtraArguments>): string {
      try {
         const { isParameter } = selection;
         const { alignmentType } = extraArgument;
         const currentRange = this.editorService.getRange();

         return this.getAlignedAsString(currentRange, alignmentType, isParameter);
      } catch (e) {
         this.tryToCleanIds();
         this.editorService.logError(e, "Get Alignment Changes");
         return "";
      }
   }

   insertElementAndSaveRangeSelection(selectedInnerHTML: string, isParameter: boolean): boolean {
      try {
         const wrapperNode = this.editorService.getWrapperElementFromInnerHMTL(selectedInnerHTML);

         // Inserting a new element in position based on range selection
         this.insertNodeInCurrentRange(wrapperNode);

         // saving the new selection range to keep the cursor pointer and selection
         if (isParameter) {
            this.getNewSelectionRangeForParameter(this.PARAMETER_ID);
         } else {
            this.getNewSelectionRange();
         }

         return true;
      } catch (error) {
         this.tryToCleanIds();
         this.editorService.logError(error, "Insert Range And Save");
         return false;
      }
   }

   /**
    * --------------------------------------------------------------------------------
    *             INSERTION FUNCTIONS
    * --------------------------------------------------------------------------------
    * */

   private insertNodeInCurrentRange(wrapperNode: Element): void {
      const rootNode = this._affectedNodes[0].parentElement;
      const lastAffected = this._affectedNodes.slice(-1)[0];
      const nodesToAppend = Array.from(wrapperNode.childNodes);
      const brotherNodes = this.getAllNextSiblingNodesFromNode(lastAffected);
      const nodesToRemove = [...this._affectedNodes, ...brotherNodes];

      this.removeAllChildFromParent(rootNode, nodesToRemove);
      this.appendAllChildren(rootNode, [...nodesToAppend, ...brotherNodes]);
   }

   private getNewSelectionRange(): void {
      try {
         const newRange = new Range();
         const startNode = document.getElementById(this.START_BOUNDARY_ID);
         const endNode = document.getElementById(this.END_BOUNDARY_ID);

         const startChild = this.getFirstTextNode(startNode);
         let endRangeNode;
         if (!Boolean(endNode)) {
            endRangeNode = this.getLastTextNode(startNode);
         } else {
            endRangeNode = this.getLastTextNode(endNode);
         }
         newRange.setStart(startChild, 0);
         newRange.setEnd(endRangeNode, endRangeNode.nodeValue.length);

         this.tryToCleanIds();

         const selection = window.getSelection();
         selection.removeAllRanges();
         selection.addRange(newRange);

         this.editorService.setWindowSelection(selection);
         this.editorService.setRange(newRange);
      } catch (error) {
         // then we need to add logic to handle get the correct range
         this.tryToCleanIds();
         this.editorService.logError(error, "getNewSelectionRange");
      }
   }

   private getNewSelectionRangeForParameter(parameterId: string): void {
      try {
         this.tryToCleanIds();

         const parameterElement = document.getElementById(parameterId);
         this.editorService.setTemplateContextElement(parameterElement);
         this.editorService.setSelectionForParameter(parameterElement);
         parameterElement.removeAttribute("id");
      } catch (error) {
         // then we need to add logic to handle get the correct range
         this.editorService.logError(error, "getNewSelectionRangeForParameter");
      }
   }

   /**
    * --------------------------------------------------------------------------------
    *             ALIGNMENT FUNCTIONS
    * --------------------------------------------------------------------------------
    * */
   private getAlignedAsString(range: Range, alignment: AlignmentType, isParameter: boolean): string {
      this.initializeVariables();
      this._alignment = alignment.toLowerCase();
      this._isParameter = isParameter;
      const parameterElement = this.editorService.getTemplateContextElement();

      const startNode = isParameter ? parameterElement : range.startContainer;
      const endNode = isParameter ? parameterElement : range.endContainer;
      const rootNode = isParameter
         ? this.getRootNode(parameterElement)
         : this.getRootNode(range.commonAncestorContainer);

      this._firstNode = this.setStartBoundaryRecursive(startNode, startNode, rootNode);
      this._lastNode = this.setEndBoundaryRecursive(endNode, endNode, rootNode);

      const nodesToWork = this.getNodeToAffect(this._firstNode, this._lastNode);
      this._affectedNodes = nodesToWork;
      const auxRootNode = this.getAuxRoot(nodesToWork);

      const result = this.getRecursiveStyle(auxRootNode.firstChild, auxRootNode, "KEEP-STYLE");
      console.log({ result });
      return result;
   }

   private getRecursiveStyle(currentNode: Node, lastSeen: Node, state: StylingStatus): string {
      if (!Boolean(currentNode) && this._stackParentNodes.length > 0) {
         return this.getRecursiveStyle(lastSeen, lastSeen, state);
      }

      if (!Boolean(currentNode)) {
         return "";
      }

      if (this.isAuxRootNode(currentNode) && this._stackParentNodes.length > 0) {
         const itemStack = this.decrementStack();
         return this.getCloseTag(itemStack) + this.getRecursiveStyle(lastSeen.nextSibling, currentNode, state);
      }

      if (this.isAuxRootNode(currentNode)) {
         return this.getRecursiveStyle(lastSeen.nextSibling, currentNode, state);
      }

      if (this.isBR(currentNode)) {
         let newState: StylingStatus = this.isStartBoundary(currentNode) ? "STYLING" : state;
         let newAlignment = newState === "STYLING" ? this._alignment : "left";

         let brReplacement = this.getCloseAndOpenTagsAsString(
            this.isStartBoundary(currentNode),
            this.isEndBoundary(currentNode),
            this._stackParentNodes,
            newAlignment,
            newState
         );

         // update STACK  if is START BOUNDARY
         if (this.isStartBoundary(currentNode)) {
            const style = `margin: 0; text-align: ${this._alignment};`;
            const itemNode = new StackItemNode("p", style, (<Element>currentNode).id);

            if (this._stackParentNodes.length > 0) {
               this.replaceFirstStackNode(itemNode); // only for BR increment if START BOUNDARY
            } else {
               this.incrementStack(itemNode);
            }
         }
         // end

         // if is END BOUNDARY
         if (this.isEndBoundary(currentNode)) {
            newState = "KEEP-STYLE";
         }

         if (Boolean(currentNode.nextSibling)) {
            if(this.isTextEmptyValue(currentNode.nextSibling) && this.isBR(currentNode.nextSibling.nextSibling)){
              return brReplacement +'&nbsp;'+ this.getRecursiveStyle(currentNode.nextSibling, currentNode, newState)
            }
            return brReplacement + this.getRecursiveStyle(currentNode.nextSibling, currentNode, newState);
         } else {
            return brReplacement + this.getRecursiveStyle(currentNode.parentNode, currentNode, newState);
         }
      }

      // section there are direct children of ROOT NODE
      if (
         this.hasParentIdEqualTo(currentNode, this.AUX_ROOT_ID) &&
         this._stackParentNodes.length === 0 &&
         !this.isDiv(currentNode) &&
         !this.isP(currentNode) &&
         (!Boolean(currentNode.previousSibling) ||
            this.isP(currentNode.previousSibling) ||
            this.isDiv(currentNode.previousSibling))
      ) {
         // checking if is START BOUNDARY
         let newState: StylingStatus = this.isStartBoundary(currentNode) ? "STYLING" : state;
         let newAlignment = newState === "STYLING" ? this._alignment : "left";
         let stackItem: StackItemNode = new StackItemNode("p", `margin: 0; text-align: ${newAlignment};`);
         const defaultStyle = `margin: 0; text-align: left;`;

         if (this.isStartBoundary(currentNode)) {
            const style = `margin: 0; text-align: ${newAlignment};`;
            // adding default style to be use when change from STYLING to KEEP-STYLE
            stackItem = new StackItemNode("p", style, (<Element>currentNode).id, defaultStyle);
         }

         // when is SPAN element START_NODE
         if (this.isStartBoundary(currentNode) && this.isSPAN(currentNode)) {
            this.incrementStack(stackItem);
            return this.getOpenTag(stackItem) + this.getRecursiveStyle(currentNode.nextSibling, currentNode, newState);
         }

         // end checking if is START BOUNDARY
         this.incrementStack(stackItem);
         return this.getOpenTag(stackItem) + this.getRecursiveStyle(currentNode, currentNode, newState);
      }

      if (this.isText(currentNode) || this.isTemplateContext(currentNode)) {
         if (Boolean(currentNode.nextSibling)) {
            return (
               this.getNodeValue(currentNode, this._isParameter) +
               this.getRecursiveStyle(currentNode.nextSibling, currentNode, state)
            );
         } else {
            return (
               this.getNodeValue(currentNode, this._isParameter) +
               this.getRecursiveStyle(currentNode.parentNode, currentNode, state)
            );
         }
      }

      // from a current node as Element go to parent nodes
      if (this.isInTheTopOfStack(currentNode)) {
         const topItem = this.decrementStack();

         if (Boolean(currentNode.nextSibling)) {
            return this.getCloseTag(topItem) + this.getRecursiveStyle(currentNode.nextSibling, currentNode, state);
         } else {
            return this.getCloseTag(topItem) + this.getRecursiveStyle(currentNode.parentNode, currentNode, state);
         }
      }

      // go from a current node as Element to child nodes
      if ((this.isDiv(currentNode) || this.isP(currentNode)) && this._stackParentNodes.length > 0) {
         const currentNodeStyle = (<Element>currentNode).getAttribute("style");
         const newState: StylingStatus = this.isStartBoundary(currentNode) ? "STYLING" : state;
         const style = newState === "KEEP-STYLE" ? currentNodeStyle : `margin: 0; text-align: ${this._alignment};`;
         const previousItemStack = this.decrementStack();

         let currentItemStack = new StackItemNode(currentNode.nodeName, style);
         if (this.isStartBoundary(currentNode)) {
            currentItemStack = new StackItemNode(currentNode.nodeName, style, (<Element>currentNode).id);
         } else {
            (<Element>currentNode).id = currentItemStack.id;
         }
         this.incrementStack(currentItemStack);

         return (
            this.getCloseTag(previousItemStack) +
            this.getOpenTag(currentItemStack) +
            this.getRecursiveStyle(currentNode.firstChild, currentNode, newState)
         );
      }

      if (this.isDiv(currentNode) || this.isP(currentNode)) {
         const currentNodeStyle = (<Element>currentNode).getAttribute("style");
         const newState: StylingStatus = this.isStartBoundary(currentNode) ? "STYLING" : state;
         const style = newState === "KEEP-STYLE" ? currentNodeStyle : `margin: 0; text-align: ${this._alignment};`;

         let currentItemStack = new StackItemNode(currentNode.nodeName, style);
         if (this.isStartBoundary(currentNode)) {
            currentItemStack = new StackItemNode(currentNode.nodeName, style, (<Element>currentNode).id);
         } else {
            (<Element>currentNode).id = currentItemStack.id;
         }
         this.incrementStack(currentItemStack);

         return (
            this.getOpenTag(currentItemStack) + this.getRecursiveStyle(currentNode.firstChild, currentNode, newState)
         );
      }

      // if reach this line the NODE is not a text
      let stackItemNode = new StackItemNode(currentNode);
      const newState: StylingStatus = this.isEndBoundary(currentNode) ? "KEEP-STYLE" : state;

      if (this.isEndBoundary(currentNode)) {
         stackItemNode = new StackItemNode(currentNode, "", (<Element>currentNode).id);
      } else {
         (<Element>currentNode).id = stackItemNode.id;
      }

      this.incrementStack(stackItemNode);
      return this.getOpenTag(stackItemNode) + this.getRecursiveStyle(currentNode.firstChild, currentNode, newState);
   }

   /**
    * --------------------------------------------------------------------------------
    *           Extra Functions
    * --------------------------------------------------------------------------------
    * */
   private decrementStack(): StackItemNode {
      if (this._stackParentNodes.length !== 0) {
         return this._stackParentNodes.pop();
      }

      throw new Error(`Can not decrement stack is empty`);
   }

   private incrementStack(stackItem: StackItemNode): void {
      this._stackParentNodes.push(stackItem);
   }

   private replaceFirstStackNode(stackItem: StackItemNode): void {
      const previousStyle = this._stackParentNodes[0].style;
      const previousId = this._stackParentNodes[0].id;
      const newStackItem = new StackItemNode(stackItem.nodeName, stackItem.style, previousId, previousStyle);
      this._stackParentNodes[0] = newStackItem;
   }

   private getNodeValue(node: Node, isParameter: boolean): string {
      if (this.isText(node)) {
         return node.nodeValue;
      }

      if (this.isTemplateContext(node) && isParameter) {
         return this.getNodeValueForParameter(node, this.PARAMETER_ID);
      }

      return (<Element>node).outerHTML;
   }

   private getNodeValueForParameter(node: Node, parameterId: string): string {
      const element = <Element>node;
      element.id = parameterId;
      return element.outerHTML;
   }

   private getOpenTag(itemNode: StackItemNode, customStyle?: string): string {
      const boundaryId = itemNode.isBoundary ? `id="${itemNode.id}"` : "";

      if (Boolean(customStyle)) {
         return `<${itemNode.nodeName} ${boundaryId} style="${customStyle}">`;
      }

      if (itemNode.style === "") {
         return `<${itemNode.nodeName} ${boundaryId}>`;
      }
      return `<${itemNode.nodeName} ${boundaryId} style="${itemNode.style}">`;
   }

   private getCloseTag(itemNode: StackItemNode): string {
      return `</${itemNode.nodeName}>`;
   }

   private isInTheTopOfStack(node: Node): boolean {
      if (this._stackParentNodes.length === 0) {
         return false;
      }

      const topItem = this._stackParentNodes.slice(-1)[0];
      return topItem.id === (<Element>node).id;
   }

   private getCloseAndOpenTagsAsString(
      isStartBoundaryBR: boolean,
      isEndBoundary: boolean,
      stack: Array<StackItemNode>,
      alignment: string,
      stylingState: StylingStatus
   ): string {
      let result = "";
      const openTagStartBoundary = `<p id="${this.START_BOUNDARY_ID}" style="margin: 0; text-align: ${alignment};">`;
      const defaultStyle = `margin: 0; text-align: left;`;

      if (stack.length === 0 && isStartBoundaryBR) {
         return (result = openTagStartBoundary);
      }

      if (stack.length === 0 && isEndBoundary) {
         return `</p>`;
      }

      if (stack.length === 0) {
         return `<p style="margin: 0; text-align: ${alignment};">&nbsp</p>`;
      }

      if (stack.length > 0) {
         stack.forEach((parentNode, index) => {
            let openTag = `<${parentNode.nodeName}>`;

            if (Boolean(parentNode.style) && stylingState === "STYLING") {
               openTag = `<${parentNode.nodeName} style="${parentNode.style}">`;
            }

            if (stylingState === "KEEP-STYLE") {
               const style = Boolean(parentNode.previousStyle) ? parentNode.previousStyle : parentNode.style;
               openTag = `<${parentNode.nodeName} style="${style}">`;
            }

            if (isStartBoundaryBR && index === 0) {
               openTag = openTagStartBoundary;
            }

            if (isEndBoundary && index === 0) {
               const { previousStyle } = parentNode;
               openTag = `<p style="${previousStyle === "" ? defaultStyle : previousStyle}">`;
            }

            const closeTag = `</${parentNode.nodeName}>`;
            result = closeTag + result + openTag;
         });
      }

      return result;
   }

   private getNodeToAffect(firstNode: Node, lastNode: Node): Array<Node> {
      const rootNode = this.getRootNode(firstNode);
      let nodes: Array<Node> = [];
      let status: SelectionType = "OUT_SELECTION";
      const children = Array.from(rootNode.childNodes);

      for (const child of children) {
         if ((child === firstNode || child.contains(firstNode)) && (child === lastNode || child.contains(lastNode))) {
            status = "OUT_SELECTION";
            nodes.push(child);
            break;
         }
         if (child === lastNode || child.contains(lastNode)) {
            status = "OUT_SELECTION";
            nodes.push(child);
            break;
         }

         if (child === firstNode || child.contains(firstNode)) {
            status = "IN_SELECTION";
            nodes.push(child);
            continue;
         }

         if (status === "IN_SELECTION") {
            nodes.push(child);
         }
      }

      return nodes;
   }

   private getAuxRoot(nodes: Node[]): Node {
      const clonedNodeList = nodes.map((node) => node.cloneNode(true));
      const auxiliarRoot = document.createElement("div");
      auxiliarRoot.id = this.AUX_ROOT_ID;

      clonedNodeList.forEach((clonedNode) => {
         auxiliarRoot.appendChild(clonedNode);
      });

      return auxiliarRoot;
   }

   private isAuxRootNode(node: Node): boolean {
      return (
         !this.isText(node) &&
         Boolean(<Element>node) &&
         Boolean((<Element>node).id) &&
         (<Element>node).id === this.AUX_ROOT_ID
      );
   }

   private setStartBoundaryRecursive(node: Node, lastSeen: Node, root: Node): Node {
      if (this.isDiv(node.previousSibling) || this.isP(node.previousSibling)) {
         const spanNode = document.createElement("span");
         spanNode.id = this.START_BOUNDARY_ID;
         (<Element>node.previousSibling).insertAdjacentElement("afterend", spanNode);
         return spanNode;
      }

      if (this.isBR(node.previousSibling)) {
         (<Element>node.previousSibling).id = this.START_BOUNDARY_ID;
         return node.previousSibling;
      }

      if (
         !this.isText(node) &&
         !this.isTemplateContext(node) &&
         Boolean(node.lastChild) &&
         node.firstChild !== lastSeen
      ) {
         return this.setStartBoundaryRecursive(node.lastChild, node, root);
      }

      if (Boolean(node.previousSibling)) {
         return this.setStartBoundaryRecursive(node.previousSibling, node, root);
      }

      if (this.isP(node.parentNode) || this.isDiv(node.parentNode) || node.parentNode === root) {
         if (!this.hasNgModelAttribute(node.parentNode)) {
            (<Element>node.parentNode).id = this.START_BOUNDARY_ID;
         } else {
            const htmlbrElement = document.createElement("br");
            htmlbrElement.id = this.START_BOUNDARY_ID;
            this.addNodeAsFirstChild(node.parentNode, htmlbrElement);
            return htmlbrElement;
         }

         return node;
      }

      if (Boolean(node.parentNode.previousSibling)) {
         return this.setStartBoundaryRecursive(node.parentNode.previousSibling, node, root);
      }

      return this.setStartBoundaryRecursive(node.parentNode, node, root);
   }

   private setEndBoundaryRecursive(node: Node, lastSeen: Node, root: Node): Node {
      if (this.isDiv(node.nextSibling) || this.isP(node.nextSibling)) {
         const spanNode = document.createElement("span");
         spanNode.id = this.END_BOUNDARY_ID;
         (<Element>node.nextSibling).insertAdjacentElement("beforebegin", spanNode);
         return spanNode;
      }

      if (this.isBR(node.nextSibling)) {
         (<Element>node.nextSibling).id = this.END_BOUNDARY_ID;
         return node.nextSibling;
      }

      if (
         !this.isText(node) &&
         !this.isTemplateContext(node) &&
         Boolean(node.firstChild) &&
         node.lastChild !== lastSeen
      ) {
         return this.setEndBoundaryRecursive(node.firstChild, node, root);
      }

      if (Boolean(node.nextSibling)) {
         return this.setEndBoundaryRecursive(node.nextSibling, node, root);
      }

      if (this.isP(node.parentNode) || this.isDiv(node.parentNode) || node.parentNode === root) {
         const spanNode = document.createElement("span");
         spanNode.id = this.END_BOUNDARY_ID;
         node.parentNode.appendChild(spanNode);
         return spanNode;
      }

      if (Boolean(node.parentNode.nextSibling)) {
         return this.setEndBoundaryRecursive(node.parentNode.nextSibling, node, root);
      }

      return this.setEndBoundaryRecursive(node.parentNode, node, root);
   }

   private isStartBoundary(node: Node): boolean {
      return Boolean(node) && Boolean((<Element>node).id) && (<Element>node).id === this.START_BOUNDARY_ID;
   }

   private isEndBoundary(node: Node): boolean {
      return Boolean(node) && Boolean((<Element>node).id) && (<Element>node).id === this.END_BOUNDARY_ID;
   }

   private addNodeAsFirstChild(parent: ParentNode & Node, nodeToAdd: Node): void {
      const { firstChild } = parent;
      const allSiblingNodes = this.getAllNextSiblingNodesFromNode(firstChild);
      this.removeAllChildFromParent(parent, [firstChild, ...allSiblingNodes]);
      this.appendAllChildren(parent, [nodeToAdd, firstChild, ...allSiblingNodes]);
   }

   private tryToCleanIds(): void {
      const startNode = document.getElementById(this.START_BOUNDARY_ID);
      const endNode = document.getElementById(this.END_BOUNDARY_ID);

      if (Boolean(startNode)) {
         startNode.removeAttribute("id");
      }
      if (Boolean(endNode)) {
         endNode.remove();
      }
   }

   private initializeVariables(): void {
      this._stackParentNodes = [];
      this._affectedNodes = [];
      this._firstNode = null;
      this._lastNode = null;
      this._isParameter = false;
      this._alignment = "";
   }
}
